import React, { useEffect, useState } from 'react';
import Card from '../../components/ui/Card';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import Button from '../../components/ui/Button';
import ReviewForm from '../../components/forms/ReviewForm';
import { 
  Star, 
  User, 
  MessageSquare, 
  Calendar,
  BarChart3,
  TrendingUp,
  Filter,
  Search,
  Plus,
  Eye,
  EyeOff
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { dataHelpers } from '../../lib/supabase';
import toast from 'react-hot-toast';

const ReviewsPage = () => {
  const { company } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(0); // 0 = todas, 1-5 = estrelas
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState({
    startDate: '',
    endDate: ''
  });
  const [showReviewForm, setShowReviewForm] = useState(false);

  useEffect(() => {
    if (company?.id) {
      loadReviews();
      loadStats();
    }
  }, [company, filter, dateFilter]);

  const loadReviews = async () => {
    try {
      setLoading(true);
      const filters = {
        rating: filter || null,
        startDate: dateFilter.startDate || null,
        endDate: dateFilter.endDate || null
      };
      
      const reviewsData = await dataHelpers.getReviews(company.id, filters);
      setReviews(reviewsData || []);
    } catch (error) {
      console.error('Error loading reviews:', error);
      toast.error('Erro ao carregar avaliações');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const filters = {
        startDate: dateFilter.startDate || null,
        endDate: dateFilter.endDate || null
      };
      
      const statsData = await dataHelpers.getReviewStats(company.id, filters);
      setStats(statsData);
    } catch (error) {
      console.error('Error loading review stats:', error);
    }
  };

  const handleCreateReview = async (reviewData) => {
    try {
      await dataHelpers.createReview(company.id, reviewData);
      await loadReviews();
      await loadStats();
      setShowReviewForm(false);
      toast.success('Avaliação criada com sucesso!');
    } catch (error) {
      console.error('Error creating review:', error);
      throw error;
    }
  };

  // Filtro por texto
  const filteredReviews = reviews.filter(review => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      review.parent_name?.toLowerCase().includes(searchLower) ||
      review.comment?.toLowerCase().includes(searchLower) ||
      review.student?.name?.toLowerCase().includes(searchLower)
    );
  });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Star
        key={index}
        className={`w-4 h-4 ${
          index < rating 
            ? 'text-yellow-400 fill-current' 
            : 'text-gray-300'
        }`}
      />
    ));
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <Star className="w-7 h-7 text-yellow-500 mr-3" />
          Avaliações dos Responsáveis
        </h1>
        <Button
          onClick={() => setShowReviewForm(true)}
          variant="primary"
          icon={Plus}
        >
          Nova Avaliação
        </Button>
      </div>

      {/* Estatísticas */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card className="p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-blue-100">
                <MessageSquare className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.total}</p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-yellow-100">
                <Star className="w-6 h-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Média</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {stats.average.toFixed(1)}
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-green-100">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">5 Estrelas</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.distribution[5]}</p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-red-100">
                <BarChart3 className="w-6 h-6 text-red-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">1-2 Estrelas</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {stats.distribution[1] + stats.distribution[2]}
                </p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Filtros */}
      <Card className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Buscar
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por responsável, aluno ou comentário..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Data Inicial
            </label>
            <input
              type="date"
              value={dateFilter.startDate}
              onChange={(e) => setDateFilter(prev => ({ ...prev, startDate: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Data Final
            </label>
            <input
              type="date"
              value={dateFilter.endDate}
              onChange={(e) => setDateFilter(prev => ({ ...prev, endDate: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        <div className="mt-4 flex items-center space-x-2">
          <span className="text-gray-700 font-medium">Filtrar por nota:</span>
          <button
            className={`px-3 py-1 rounded-full border text-sm font-semibold ${
              filter === 0 ? 'bg-blue-500 text-white border-blue-500' : 'bg-white text-gray-700 border-gray-300'
            }`}
            onClick={() => setFilter(0)}
          >
            Todas
          </button>
          {[5,4,3,2,1].map(star => (
            <button
              key={star}
              className={`flex items-center px-3 py-1 rounded-full border text-sm font-semibold space-x-1 ${
                filter === star ? 'bg-yellow-400 text-white border-yellow-400' : 'bg-white text-gray-700 border-gray-300'
              }`}
              onClick={() => setFilter(star)}
            >
              <Star className="w-4 h-4 mr-1" />
              <span>{star}</span>
            </button>
          ))}
        </div>
      </Card>
      {/* Lista de Avaliações */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : (
        <Card>
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">
              Avaliações ({filteredReviews.length})
            </h3>
          </div>
          
          {filteredReviews.length === 0 ? (
            <div className="p-12 text-center">
              <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {reviews.length === 0 ? 'Nenhuma avaliação recebida ainda' : 'Nenhuma avaliação encontrada'}
              </h3>
              <p className="text-gray-600">
                {reviews.length === 0 
                  ? 'As avaliações dos responsáveis aparecerão aqui.' 
                  : 'Tente ajustar os filtros para encontrar avaliações.'
                }
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredReviews.map((review) => (
                <div key={review.id} className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <User className="w-5 h-5 text-blue-500 mr-2" />
                        <span className="font-semibold text-gray-900">
                          {review.parent?.name || review.parent_name}
                        </span>
                        {review.student && (
                          <span className="ml-2 text-sm text-gray-500">
                            • Aluno: {review.student.name} ({review.student.registration_number})
                          </span>
                        )}
                      </div>
                      
                      <div className="flex items-center mb-3">
                        <div className="flex space-x-1 mr-3">
                          {renderStars(review.rating)}
                        </div>
                        <span className="text-sm font-medium text-gray-700">
                          {review.rating}/5
                        </span>
                      </div>
                      
                      {review.comment && (
                        <blockquote className="text-gray-700 italic mb-3 pl-4 border-l-4 border-gray-200">
                          "{review.comment}"
                        </blockquote>
                      )}
                      
                      <div className="flex items-center text-xs text-gray-500">
                        <Calendar className="w-4 h-4 mr-1" />
                        {formatDate(review.created_at)}
                      </div>
                    </div>
                    
                    <div className="ml-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        review.rating >= 4 
                          ? 'bg-green-100 text-green-800' 
                          : review.rating >= 3 
                          ? 'bg-yellow-100 text-yellow-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {review.rating >= 4 ? 'Positiva' : review.rating >= 3 ? 'Neutra' : 'Negativa'}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Modal de Nova Avaliação */}
      {showReviewForm && (
        <ReviewForm
          onSubmit={handleCreateReview}
          onCancel={() => setShowReviewForm(false)}
        />
      )}
    </div>
  );
};

export default ReviewsPage; 